import React, { useCallback, useEffect, useState } from 'react';
import RecipeInterview, { RecipeValues, toRecipeBody } from '../../components/RecipeInterview';
import { FileDetailModal, MoFile } from '../../components/FileDetailModal';

// MoBoard 인스타그램 수집 콜렉터 관리 — /admin/moboard (requireAdmin).
// 수집 주기/토글 · 지금수집 · 메타 사용률 · 소유증명/동의 · 장소별 헬스 · 실행 이력.
// + 대신 승인(sudo): 전 운영처 수집본을 한 곳에 모아 관리자가 소유주 자격으로 승인/거절.

// 전 운영처 수집(source='api') 승인 대기 — GET /admin/moboard/pending.
interface PendingItem {
  id: number;
  fileHash: string | null;
  eventStartAt: string | null;
  promoStartAt: string | null;
  eventLabel: string | null;
  overlayPosition: number;
  createdAt: string;
  ownerHash: string;
  ownerName: string | null;
  ownerHandle: string | null;
  filename: string | null;
  thumbnailPath: string | null;
  duration: number | null;
  mimeType: string | null;
  fileSize: number | null;
  expiresAt: string | null;
  transcodeStatus?: string | null;
  source?: string | null;
  eventEndAt?: string | null;
}

// 변환 실패(승인 후 게재 준비 실패) — GET /admin/moboard/failed.
interface FailedItem {
  fileHash: string;
  filename: string | null;
  thumbnailPath: string | null;
  transcodeError: string | null;
  failedAt: string | null;
  ownerHash: string;
  ownerName: string | null;
  ownerHandle: string | null;
}

interface Health {
  last_success_at: string | null;
  last_call_at: string | null;
  last_error_code: string | null;
  api_status: string;
  consecutive_fail: number;
}
interface Venue {
  ownerHash: string;
  name: string | null;
  handle: string | null;
  instagramUsername: string | null;
  instagramUserId: string | null;
  consent: boolean;
  consentAt: string | null;
  health: Health | null;
}
interface Run {
  id: number;
  started_at: string;
  finished_at: string | null;
  mode: string;
  venues_total: number;
  venues_attempted: number;
  new_media: number;
  queued: number;
  errors: number;
  note: string | null;
}
interface Status {
  config: { enabled: boolean; disabledReason: string | null; graphVersion: string };
  settings: {
    intervalHours: number; enabled: boolean; minManualIntervalMin: number;
    backoffUntil: string | null; faultState: string | null; faultAt: string | null;
    nextRunAt: string | null; updatedAt: string;
  };
  usage: { appPct: number | null; bucPct: number | null };
  schedulerAlive: boolean;
  venues: Venue[];
  runs: Run[];
}

const fmt = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' });
};
const fmtTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleTimeString('ko-KR', { timeZone: 'Asia/Seoul', hour: '2-digit', minute: '2-digit' });
};
const summarizeRun = (r: Run): string =>
  `${r.mode === 'manual' ? '수동' : '자동'} · 장소 ${r.venues_attempted}/${r.venues_total} · 신규 ${r.new_media} · 대기열 ${r.queued} · 오류 ${r.errors}${r.note ? ` · ${r.note}` : ''}`;

const DISABLED_MSG: Record<string, string> = {
  'flag-off': 'MOBOARD_IG_ENABLED 미설정 — 서버 환경변수로 켜야 수집이 돕니다.',
  'no-token': '토큰 미설정(MOBOARD_IG_TOKEN) — 서버에 토큰을 주입해야 합니다.',
  'no-app-secret': '앱 시크릿 미설정(MOBOARD_IG_APP_SECRET) — 설정 후 수집 가능합니다.',
};

const MoboardCollector: React.FC = () => {
  const [st, setSt] = useState<Status | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [running, setRunning] = useState(false);
  // 편집 중 설정(저장 전).
  const [editInterval, setEditInterval] = useState<number>(2);
  const [editEnabled, setEditEnabled] = useState<boolean>(true);
  // 소유증명 인라인 폼: ownerHash → {username, code}.
  const [proof, setProof] = useState<Record<string, { username: string; code: string | null; busy: boolean; e: string | null }>>({});
  // 대신 승인 큐.
  const [pending, setPending] = useState<PendingItem[]>([]);
  const [approveTarget, setApproveTarget] = useState<PendingItem | null>(null);
  const [previewItem, setPreviewItem] = useState<PendingItem | null>(null);

  const [failed, setFailed] = useState<FailedItem[]>([]);

  const loadPending = useCallback(async () => {
    try {
      const r = await fetch('/admin/moboard/pending', { credentials: 'include' });
      const j = await r.json();
      if (j.success) setPending(j.data.items ?? []);
    } catch { /* 승인 대기 조회 실패는 페이지 치명 아님 */ }
  }, []);

  const loadFailed = useCallback(async () => {
    try {
      const r = await fetch('/admin/moboard/failed', { credentials: 'include' });
      const j = await r.json();
      if (j.success) setFailed(j.data.items ?? []);
    } catch { /* 변환 실패 조회 실패는 페이지 치명 아님 */ }
  }, []);

  const recollect = async (item: FailedItem) => {
    if (!window.confirm(`${item.ownerName || item.ownerHash}의 "${item.filename || '포스터'}"를 다시 수집할까요?\n실패한 파일을 지우고, 다음 수집 때 그 게시물을 다시 내려받습니다.`)) return;
    const r = await fetch('/admin/moboard/recollect', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileHash: item.fileHash }),
    });
    const j = await r.json().catch(() => ({}));
    if (r.ok && j?.success) { setFailed(f => f.filter(x => x.fileHash !== item.fileHash)); setMsg('재수집 대상으로 되돌렸습니다 — 다음 수집 때 다시 내려받습니다.'); }
    else alert(j?.error || '재수집 준비에 실패했습니다.');
  };

  const discard = async (item: FailedItem) => {
    if (!window.confirm(`${item.ownerName || item.ownerHash}의 "${item.filename || '포스터'}"를 폐기할까요? (재수집하지 않음)`)) return;
    const r = await fetch('/admin/moboard/discard', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileHash: item.fileHash }),
    });
    const j = await r.json().catch(() => ({}));
    if (r.ok && j?.success) setFailed(f => f.filter(x => x.fileHash !== item.fileHash));
    else alert(j?.error || '폐기에 실패했습니다.');
  };

  const load = useCallback(async (): Promise<Status | null> => {
    try {
      const r = await fetch('/admin/moboard/status', { credentials: 'include' });
      const j = await r.json();
      if (!j.success) { setErr(j.error || '상태 조회 실패'); return null; }
      setErr(null);
      setSt(j.data);
      setEditInterval(j.data.settings.intervalHours);
      setEditEnabled(j.data.settings.enabled);
      return j.data as Status;
    } catch {
      setErr('상태 조회 중 오류');
      return null;
    }
  }, []);

  useEffect(() => {
    load();
    loadPending();
    // 변환 실패는 폴링하지 않는다 — 실패 시 승인한 관리자에게 시스템 메시지가 가고(서버),
    // 그 안내로 이 페이지에 와서 확인·복구한다. 목록은 진입 시 1회 + 복구 동작 후에만 갱신.
    loadFailed();
    const t = setInterval(() => { load(); loadPending(); }, 30_000);
    return () => clearInterval(t);
  }, [load, loadPending, loadFailed]);

  // 문진 프리필 — 소유주 본인 승인(Dashboard)과 동일 규칙: 종료=expiresAt ?? 행사+1h, 시작은 문진 파생.
  const approvePrefill = (item: PendingItem) => ({
    eventStartAt: item.eventStartAt,
    eventEndAt: item.expiresAt
      ?? (item.eventStartAt ? new Date(new Date(item.eventStartAt).getTime() + 3600000).toISOString() : null),
    promoStartAt: item.promoStartAt,
    promoEndAt: null,
    placement: 'slot' as 'box' | 'slot',
    isPublic: false,
  });

  const submitApprove = async (values: RecipeValues) => {
    if (!approveTarget) return { ok: false, error: '대상 없음' };
    const r = await fetch(`/admin/moboard/pending/${approveTarget.id}/approve`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recipe: toRecipeBody(values) }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || !j?.success) return { ok: false, error: j?.error || '승인에 실패했습니다.' };
    setPending(p => p.filter(x => x.id !== approveTarget.id));
    return { ok: true, receipt: j.data?.receipt ?? { placements: [], warnings: [] } };
  };

  const rejectPending = async (item: PendingItem) => {
    if (!window.confirm(`${item.ownerName || item.ownerHash}의 "${item.filename || '포스터'}"를 거절할까요? (수집 파일 삭제)`)) return;
    const r = await fetch(`/admin/moboard/pending/${item.id}/reject`, { method: 'POST', credentials: 'include' });
    const j = await r.json().catch(() => ({}));
    if (r.ok && j?.success) setPending(p => p.filter(x => x.id !== item.id));
    else alert(j?.error || '거절에 실패했습니다.');
  };

  const saveSettings = async () => {
    if (!st) return;
    setSavingSettings(true); setMsg(null);
    try {
      const r = await fetch('/admin/moboard/settings', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intervalHours: editInterval, enabled: editEnabled, updatedAt: st.settings.updatedAt }),
      });
      const j = await r.json();
      if (!j.success) { alert(j.error || '저장 실패'); await load(); return; }
      setMsg('설정 저장됨');
      await load();
    } finally { setSavingSettings(false); }
  };

  const runNow = async (force = false) => {
    const prevTopId = st?.runs?.[0]?.id ?? 0;
    setRunning(true); setMsg('수집 요청 중…');
    try {
      const r = await fetch('/admin/moboard/run', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force }),
      });
      const j = await r.json();
      if (r.status !== 202 || !j.success) {
        setMsg(`⚠ ${j.error || '수동 수집 실패'}`);
        return;
      }
      // 서버는 fire-and-forget(202). 새 run 행이 생길 때까지 폴링해 결과를 표시.
      setMsg('수집 실행 중… (잠시만요)');
      for (let i = 0; i < 8; i++) {
        await new Promise(res => setTimeout(res, 1500));
        const data = await load();
        const top = data?.runs?.[0];
        if (top && top.id !== prevTopId) {
          setMsg(top.finished_at
            ? `✓ 수집 완료 — ${summarizeRun(top)}`
            : `수집 실행 중… — ${summarizeRun(top)}`);
          if (top.finished_at) return;
        }
      }
      setMsg('수집을 시작했습니다. 결과는 아래 「실행 이력」에서 확인하세요.');
    } finally { setRunning(false); loadPending(); }
  };

  const retry = async () => {
    await fetch('/admin/moboard/retry', { method: 'POST', credentials: 'include' });
    setMsg('오류 상태 해제됨'); await load();
  };

  const startProof = async (v: Venue) => {
    const username = proof[v.ownerHash]?.username ?? v.instagramUsername ?? '';
    if (!username) { setProof(p => ({ ...p, [v.ownerHash]: { username: '', code: null, busy: false, e: '인스타그램 아이디 입력' } })); return; }
    setProof(p => ({ ...p, [v.ownerHash]: { username, code: null, busy: true, e: null } }));
    const r = await fetch('/admin/moboard/consent/start-proof', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ownerHash: v.ownerHash, username }),
    });
    const j = await r.json();
    setProof(p => ({ ...p, [v.ownerHash]: { username, code: j.success ? j.data.code : null, busy: false, e: j.success ? null : (j.error || '발급 실패') } }));
  };

  const verifyProof = async (v: Venue) => {
    setProof(p => ({ ...p, [v.ownerHash]: { ...(p[v.ownerHash] || { username: '', code: null }), busy: true, e: null } }));
    const r = await fetch('/admin/moboard/consent/verify', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ownerHash: v.ownerHash }),
    });
    const j = await r.json();
    if (j.success) {
      setProof(p => { const n = { ...p }; delete n[v.ownerHash]; return n; });
      setMsg('소유증명·동의 완료');
      await load();
    } else {
      setProof(p => ({ ...p, [v.ownerHash]: { ...(p[v.ownerHash] || { username: '', code: null }), busy: false, e: j.error || '검증 실패' } }));
    }
  };

  // 관리자 직접 지정 — 구두/서면 동의를 관리자가 확인하고 코드 증명 없이 바로 켠다.
  const designate = async (v: Venue) => {
    const username = proof[v.ownerHash]?.username ?? v.instagramUsername ?? '';
    if (!username) { setProof(p => ({ ...p, [v.ownerHash]: { username: '', code: null, busy: false, e: '인스타그램 아이디 입력' } })); return; }
    if (!window.confirm(`구두/서면 동의를 확인했습니까? @${username} 를 '${v.name || v.ownerHash}' 로 지정하고 수집 동의를 켭니다.`)) return;
    setProof(p => ({ ...p, [v.ownerHash]: { username, code: null, busy: true, e: null } }));
    const r = await fetch('/admin/moboard/consent/designate', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ownerHash: v.ownerHash, username }),
    });
    const j = await r.json();
    if (j.success) {
      setProof(p => { const n = { ...p }; delete n[v.ownerHash]; return n; });
      setMsg('직접 지정·동의 완료'); await load();
    } else {
      setProof(p => ({ ...p, [v.ownerHash]: { username, code: null, busy: false, e: j.error || '지정 실패' } }));
    }
  };

  const revoke = async (v: Venue) => {
    if (!window.confirm(`${v.name || v.ownerHash} 의 수집 동의를 철회할까요?`)) return;
    await fetch('/admin/moboard/consent/revoke', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ownerHash: v.ownerHash }),
    });
    setMsg('동의 철회됨'); await load();
  };

  if (!st) {
    return <div className="p-6">{err ? <span className="text-red-600">{err}</span> : '불러오는 중…'}</div>;
  }

  const s = st.settings;
  const backoffActive = !!s.backoffUntil && new Date(s.backoffUntil).getTime() > Date.now();
  const runDisabled = running || !st.config.enabled || !!s.faultState || backoffActive;
  const pctClass = (p: number | null) => (p != null && p >= 80 ? 'bg-yellow-200 font-semibold' : '');

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold">MoBoard 인스타그램 수집</h1>

      {st.config.disabledReason && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          {DISABLED_MSG[st.config.disabledReason] || '수집 비활성'} 설정은 저장할 수 있으나, 서버에 토큰이 설정돼야 실제 수집이 시작됩니다.
        </div>
      )}
      {!st.schedulerAlive && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          스케줄러 응답 없음 (다음 실행 예정 시각이 지났습니다). 서버 로그를 확인하세요.
        </div>
      )}
      {msg && <div className="rounded border border-blue-200 bg-blue-50 p-2 text-sm text-blue-700">{msg}</div>}

      {/* 변환 실패 (확인 필요) — 승인했지만 게재 준비에 실패한 수집본. 관리자 피드백 */}
      {failed.length > 0 && (
        <section className="rounded border border-red-300 bg-red-50 p-4 space-y-2">
          <h2 className="font-semibold text-red-800">⚠ 변환 실패 — 확인 필요 ({failed.length})</h2>
          <p className="text-xs text-red-700">
            승인했지만 게재 준비(이미지 변환)에 실패한 수집본입니다. 「다시 수집」을 누르면 실패한 파일을 지우고
            다음 수집 때 그 게시물을 다시 내려받습니다.
          </p>
          <div className="space-y-2">
            {failed.map(item => (
              <div key={item.fileHash} className="flex items-center gap-3 border-b border-red-200 pb-2">
                <div className="w-20 h-12 rounded bg-white flex-shrink-0 overflow-hidden flex items-center justify-center border border-red-200">
                  {item.thumbnailPath
                    ? <img src={`/mofile/thumbnail/${item.thumbnailPath}`} alt="" className="w-full h-full object-cover" />
                    : <span className="text-[10px] text-gray-400">미리보기</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{item.ownerName || item.ownerHash}
                    {item.ownerHandle && <span className="text-gray-400 font-normal"> @{item.ownerHandle}</span>}
                  </div>
                  <div className="text-xs text-gray-600 truncate">
                    {item.filename || '(제목 없음)'}
                    {item.transcodeError && <span className="text-red-600"> · {item.transcodeError}</span>}
                    {item.failedAt && <> · {fmt(item.failedAt)}</>}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => recollect(item)}
                    className="px-3 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700">다시 수집</button>
                  <button onClick={() => discard(item)}
                    className="px-3 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200">폐기</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 주기 설정 */}
      <section className="rounded border border-gray-200 p-4 space-y-3">
        <h2 className="font-semibold">자동 수집 설정</h2>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2">
            주기
            <select className="border rounded px-2 py-1" value={editInterval} onChange={e => setEditInterval(Number(e.target.value))}>
              {[1, 2, 3, 6, 12, 24].map(h => <option key={h} value={h}>{h}시간</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={editEnabled} onChange={e => setEditEnabled(e.target.checked)} />
            자동 수집 켜기
          </label>
          <button className="px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-50" disabled={savingSettings} onClick={saveSettings}>
            {savingSettings ? '저장 중…' : '저장'}
          </button>
          <span className="text-sm text-gray-500">다음 자동 실행: {fmt(s.nextRunAt)}</span>
        </div>
      </section>

      {/* 지금 수집 + 상태 */}
      <section className="rounded border border-gray-200 p-4 space-y-3">
        <h2 className="font-semibold">지금 수집 / 상태</h2>
        <div className="flex flex-wrap items-center gap-4">
          <button className="px-3 py-1 rounded bg-emerald-600 text-white disabled:opacity-50" disabled={runDisabled} onClick={() => runNow(false)}>
            {running ? '수집 중…' : '지금 수집'}
          </button>
          {!st.config.enabled && <span className="text-sm text-gray-500">토큰 미설정 — 수집 불가</span>}
          {s.faultState && (
            <span className="text-sm text-red-600">
              오류({s.faultState === 'token' ? '토큰' : '권한'}) — 재시도 필요
              <button className="ml-2 px-2 py-0.5 rounded border border-red-300 text-red-700" onClick={retry}>재시도</button>
            </span>
          )}
          {backoffActive && <span className="text-sm text-amber-700">메타 제한 — {fmtTime(s.backoffUntil)}까지 대기</span>}
        </div>
        <div className="text-sm">
          {st.runs[0]
            ? <span className="text-gray-700">마지막 수집: <span className="text-gray-900">{fmt(st.runs[0].started_at)}</span> · {summarizeRun(st.runs[0])}{!st.runs[0].finished_at && <span className="text-emerald-700"> (실행 중)</span>}</span>
            : <span className="text-gray-400">아직 수집 실행 이력이 없습니다.</span>}
        </div>
        <div className="flex flex-wrap gap-6 text-sm">
          <span className={`px-2 py-0.5 rounded ${pctClass(st.usage.bucPct)}`}>BUC 사용률: {st.usage.bucPct != null ? `${st.usage.bucPct}%` : '—'}</span>
          <span className={`px-2 py-0.5 rounded ${pctClass(st.usage.appPct)}`}>App 사용률: {st.usage.appPct != null ? `${st.usage.appPct}%` : '—'}</span>
          <span className="text-gray-500">Graph {st.config.graphVersion}</span>
        </div>
      </section>

      {/* 대신 승인 큐 — 전 운영처 수집본을 한 곳에서 관리자가 소유주 자격으로 승인/거절 */}
      <section className="rounded border border-gray-200 p-4 space-y-2">
        <h2 className="font-semibold">
          승인 대기 (대신 승인) <span className="text-sm font-normal text-gray-400">({pending.length})</span>
        </h2>
        <p className="text-xs text-gray-500">
          수집된 포스터를 운영처를 대신해 승인합니다. 승인 창은 운영처 본인 화면과 동일하며,
          배치(요일 박스·빈 슬롯·업로드만)·행사 일시·노출 기간을 그대로 정할 수 있습니다. 승인/거절은 활동 로그에 관리자 이름으로 남습니다.
        </p>
        {pending.length === 0 ? (
          <p className="text-sm text-gray-400 py-2">승인 대기 중인 수집 포스터가 없습니다.</p>
        ) : (
          <div className="space-y-2">
            {pending.map(item => (
              <div key={item.id} className="flex items-center gap-3 border-b pb-2">
                <button type="button" onClick={() => item.fileHash && setPreviewItem(item)} title="클릭하여 확인"
                  className="relative w-20 h-12 rounded bg-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center group">
                  {item.thumbnailPath
                    ? <img src={`/mofile/thumbnail/${item.thumbnailPath}`} alt="" className="w-full h-full object-cover" />
                    : <span className="text-[10px] text-gray-400">미리보기</span>}
                  <span className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity text-white text-lg">▶</span>
                </button>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{item.ownerName || item.ownerHash}
                    {item.ownerHandle && <span className="text-gray-400 font-normal"> @{item.ownerHandle}</span>}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {item.filename || '(제목 없음)'} · 수집 {fmt(item.createdAt)}
                    {item.eventStartAt && <> · 행사 {fmt(item.eventStartAt)}</>}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => setApproveTarget(item)}
                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">승인·송출 조건…</button>
                  <button onClick={() => rejectPending(item)}
                    className="px-3 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200">거절</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {approveTarget && (
        <RecipeInterview
          title={`${approveTarget.ownerName || approveTarget.ownerHash} — ${approveTarget.filename || '(제목 없음)'}`}
          prefill={approvePrefill(approveTarget)}
          approveMode
          onSubmit={submitApprove}
          onClose={() => setApproveTarget(null)}
        />
      )}

      {/* 대기 포스터/영상 확인 — 소유주 본인 승인 화면과 동일한 상세 모달. 남의 파일이라 readOnly
          (편집·삭제 버튼 숨김). api 수집본은 승인 전 'deferred' → 포스터 썸네일을 보여준다. */}
      {previewItem && previewItem.fileHash && (
        <FileDetailModal
          readOnly
          file={{
            title: previewItem.filename || '(제목 없음)',
            file_url: `/mofile/download/${previewItem.fileHash}?v=${previewItem.fileSize ?? 0}`,
            file_hash: previewItem.fileHash,
            file_type: previewItem.mimeType || 'video/mp4',
            created_at: previewItem.createdAt,
            thumbnail_path: previewItem.thumbnailPath || undefined,
            duration: previewItem.duration,
            event_start_at: previewItem.eventStartAt,
            event_label: previewItem.eventLabel,
            overlay_position: previewItem.overlayPosition,
            transcode_status: (previewItem.transcodeStatus ?? undefined) as MoFile['transcode_status'],
          }}
          onClose={() => setPreviewItem(null)}
          onDelete={() => setPreviewItem(null)}
          onUpdate={() => { /* readOnly — 편집 없음 */ }}
        />
      )}

      {/* 장소별 */}
      <section className="rounded border border-gray-200 p-4 space-y-2">
        <h2 className="font-semibold">협력 장소 ({st.venues.length})</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-1 pr-2">장소</th><th className="pr-2">인스타그램</th><th className="pr-2">신원</th>
                <th className="pr-2">동의</th><th className="pr-2">마지막 성공/호출</th><th className="pr-2">오류</th><th>관리</th>
              </tr>
            </thead>
            <tbody>
              {st.venues.map(v => {
                const pf = proof[v.ownerHash];
                return (
                  <tr key={v.ownerHash} className="border-b align-top">
                    <td className="py-2 pr-2">{v.name || '—'}<div className="text-gray-400">{v.handle ? `@${v.handle}` : ''}</div></td>
                    <td className="pr-2">{v.instagramUsername || '—'}</td>
                    <td className="pr-2">{v.instagramUserId ? <span className="text-emerald-700">확인됨</span> : <span className="text-gray-400">미확인</span>}</td>
                    <td className="pr-2">{v.consent
                      ? <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">동의</span>
                      : <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-500">미동의</span>}</td>
                    <td className="pr-2 text-gray-600">{fmt(v.health?.last_success_at)}<div className="text-gray-400">{fmt(v.health?.last_call_at)}</div></td>
                    <td className="pr-2">{v.health?.last_error_code ? <span className="text-red-600">{v.health.last_error_code}{v.health.consecutive_fail > 1 ? ` ×${v.health.consecutive_fail}` : ''}</span> : '—'}</td>
                    <td className="space-y-1">
                      {v.consent ? (
                        <button className="px-2 py-0.5 rounded border border-gray-300" onClick={() => revoke(v)}>동의 철회</button>
                      ) : (
                        <div className="space-y-1">
                          {!pf?.code ? (
                            <div className="flex items-center gap-1">
                              <input
                                className="border rounded px-1 py-0.5 w-28" placeholder="인스타그램 아이디"
                                defaultValue={v.instagramUsername || ''}
                                onChange={e => setProof(p => ({ ...p, [v.ownerHash]: { username: e.target.value, code: null, busy: false, e: null } }))}
                              />
                              <button className="px-2 py-0.5 rounded border border-gray-300 disabled:opacity-50" disabled={pf?.busy} onClick={() => startProof(v)}>코드 발급</button>
                              <button className="px-2 py-0.5 rounded bg-green-600 text-white disabled:opacity-50" disabled={pf?.busy} title="구두/서면 동의 확인 시 코드 게시 없이 바로 지정" onClick={() => designate(v)}>직접 지정</button>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <div className="text-xs">공연장 바이오/캡션에 코드 게시 요청: <span className="font-mono font-bold">{pf.code}</span></div>
                              <button className="px-2 py-0.5 rounded bg-blue-600 text-white disabled:opacity-50" disabled={pf.busy} onClick={() => verifyProof(v)}>검증</button>
                            </div>
                          )}
                          {pf?.e && <div className="text-xs text-red-600">{pf.e}</div>}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {st.venues.length === 0 && <tr><td colSpan={7} className="py-3 text-gray-400">인스타그램 아이디가 등록된 장소가 없습니다.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {/* 실행 이력 */}
      <section className="rounded border border-gray-200 p-4 space-y-2">
        <h2 className="font-semibold">실행 이력 (최근 20)</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-1 pr-2">시각</th><th className="pr-2">유형</th><th className="pr-2">장소</th>
                <th className="pr-2">신규</th><th className="pr-2">대기열</th><th className="pr-2">오류</th><th>비고</th>
              </tr>
            </thead>
            <tbody>
              {st.runs.map(r => (
                <tr key={r.id} className="border-b">
                  <td className="py-1 pr-2 text-gray-600">{fmt(r.started_at)}</td>
                  <td className="pr-2">{r.mode === 'manual' ? '수동' : '자동'}</td>
                  <td className="pr-2">{r.venues_attempted}/{r.venues_total}</td>
                  <td className="pr-2">{r.new_media}</td>
                  <td className="pr-2">{r.queued}</td>
                  <td className="pr-2">{r.errors > 0 ? <span className="text-red-600">{r.errors}</span> : 0}</td>
                  <td className="text-gray-500">{r.note || ''}</td>
                </tr>
              ))}
              {st.runs.length === 0 && <tr><td colSpan={7} className="py-3 text-gray-400">실행 이력이 없습니다.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {/* 소유증명·동의 절차 가이드 */}
      <section className="rounded border border-gray-200 bg-gray-50 p-4 space-y-2 text-sm text-gray-700">
        <h2 className="font-semibold text-gray-900">소유증명·동의 절차 안내</h2>
        <p className="text-gray-600">
          공연장 인스타그램 게시물을 수집하려면, 그 계정이 실제로 협력 공연장의 것임을 확인하고 수집 동의를 받아야 합니다.
          두 가지 방법이 있습니다.
        </p>
        <div className="space-y-1">
          <p className="font-medium text-gray-900">① 코드 발급 (셀프 소유증명)</p>
          <ol className="list-decimal list-inside space-y-0.5 text-gray-600">
            <li>장소 행에서 공연장 인스타그램 아이디를 입력하고 <span className="font-medium">「코드 발급」</span>을 누릅니다.</li>
            <li>표시된 일회성 코드(예: <span className="font-mono">AB12CD34</span>)를 공연장에 전달합니다.</li>
            <li>공연장이 자기 인스타그램 <span className="font-medium">프로필 소개(바이오)나 최근 게시물 캡션</span>에 그 코드를 올립니다.</li>
            <li>코드가 올라온 것을 확인한 뒤 <span className="font-medium">「검증」</span>을 누르면, 서버가 그 계정을 조회해 코드를 확인하고 계정 신원(IG user id)을 고정 + 동의를 켭니다.</li>
          </ol>
          <p className="text-gray-500 text-xs">코드는 24시간 유효, 하루 검증 3회 제한. 계정 소유를 공연장이 직접 증명하므로 가장 안전합니다.</p>
        </div>
        <div className="space-y-1">
          <p className="font-medium text-gray-900">② 직접 지정 (구두/서면 동의를 관리자가 확인)</p>
          <ol className="list-decimal list-inside space-y-0.5 text-gray-600">
            <li>이미 구두/서면으로 합의된 공연장은, 아이디 입력 후 <span className="font-medium">「직접 지정」</span>을 누릅니다.</li>
            <li>확인창에서 동의 확인을 누르면, 코드 게시 없이 바로 계정 신원을 조회·고정하고 동의를 켭니다.</li>
          </ol>
          <p className="text-gray-500 text-xs">관리자가 동의를 보증하는 경로입니다. 공개 프로페셔널 계정만 가능하며, 비공개/개인 계정이면 조회되지 않습니다.</p>
        </div>
        <p className="text-gray-500 text-xs">
          두 방법 모두 수집은 <span className="font-medium">공개 게시물만</span> 대상으로 하며, 동의를 끄면(「동의 철회」) 즉시 수집이 중단됩니다.
          수집된 포스터는 자동 게재되지 않고 <span className="font-medium">승인 대기열</span>로 들어가 사람이 확인 후 승인합니다.
        </p>
      </section>
    </div>
  );
};

export default MoboardCollector;
